import type { TOC } from '@ember/component/template-only';
import {
  PhCheckCircle,
  PhHourglass,
  PhClock,
  PhArrowCounterClockwise,
  PhWarningCircle,
} from 'ember-phosphor-icons';
import { eq } from 'tio-ui/utilities';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import { formatCurrency, formatMonthYear } from 'tio-ui/format';

/**
 * PaymentStatusCard — Displays a last or next payment with amount, date, and status
 *
 * Used on the reimagined finance/history page to show at-a-glance payment info.
 *
 * @example
 * ```gts
 * <PaymentStatusCard
 *   @title="Last Payment"
 *   @amount={{125}}
 *   @date="2026-01-15"
 *   @status="sent"
 *   @theme="violet"
 * />
 * ```
 */

type PaymentStatus = 'sent' | 'in_progress' | 'pending' | 'resent' | 'failed';
type Theme = 'violet' | 'blue' | 'emerald' | 'amber';

interface PaymentStatusCardSignature {
  Args: {
    /** Card title (e.g. "Last Payment", "Next Payment") */
    title: string;
    /** Payment amount in dollars */
    amount: number;
    /** Date string (ISO or displayable) */
    date?: string | null;
    /** Payment status */
    status: PaymentStatus;
    /** Color theme for accent */
    theme?: Theme;
  };
  Element: HTMLDivElement;
}

function statusLabel(status: PaymentStatus): string {
  switch (status) {
    case 'sent':
      return 'Payment Sent';
    case 'resent':
      return 'Resent';
    case 'failed':
      return 'Failed';
    case 'in_progress':
      return 'In Progress';
    case 'pending':
      return 'Pending';
  }
}

function statusIntent(status: PaymentStatus): 'success' | 'primary' | 'default' | 'danger' {
  switch (status) {
    case 'sent':
    case 'resent':
      return 'success';
    case 'failed':
      return 'danger';
    case 'in_progress':
      return 'primary';
    case 'pending':
      return 'default';
  }
}

/**
 * Accent theme → shared `.tio-accent-{intent}` class. The border, value color,
 * and icon-badge styling live in the `.tio-payment-card` recipe + the shared
 * `.tio-accent-{intent}` family (`addons/tio-ui/src/styles/`). Maps the
 * legacy color names to intents (violet→brand, blue→info, emerald→success,
 * amber→warning); defaults to brand.
 */
function accentClass(theme: Theme | undefined): string {
  switch (theme) {
    case 'blue':
      return 'tio-accent-info';
    case 'emerald':
      return 'tio-accent-success';
    case 'amber':
      return 'tio-accent-warning';
    case 'violet':
    default:
      return 'tio-accent-brand';
  }
}

const PaymentStatusCard: TOC<PaymentStatusCardSignature> = <template>
  {{! template-lint-disable no-bare-strings }}
  <div class="tio-payment-card {{accentClass @theme}}" ...attributes>
    <div class="tio-payment-card__header">
      {{! @weight omitted on purpose: the title intentionally renders at <Text>'s
          default weight (400); bumping it to medium is a design decision. }}
      <Text @variant="inherit" @size="sm" class="tio-payment-card__title">{{@title}}</Text>
      {{#if @date}}
        <Text @variant="inherit" @size="sm" class="tio-payment-card__date">{{formatMonthYear
            @date
          }}</Text>
      {{/if}}
    </div>

    <div class="tio-payment-card__body">
      <div class="tio-payment-card__icon">
        {{#if (eq @status "sent")}}
          <PhCheckCircle @size={{24}} />
        {{else if (eq @status "resent")}}
          <PhArrowCounterClockwise @size={{24}} />
        {{else if (eq @status "failed")}}
          <PhWarningCircle @size={{24}} />
        {{else if (eq @status "in_progress")}}
          <PhHourglass @size={{24}} />
        {{else}}
          <PhClock @size={{24}} />
        {{/if}}
      </div>
      <div class="tio-payment-card__value">{{formatCurrency @amount}}</div>
    </div>

    <div>
      <Chip @intent={{statusIntent @status}} @size="sm">
        {{statusLabel @status}}
      </Chip>
    </div>
  </div>
</template>;

export default PaymentStatusCard;
